import React, { useState, useEffect, useRef } from 'react';
import {
  X, Cpu, Plus, Trash2, Check, Settings2, RefreshCw,
  ChevronDown, ChevronUp, BarChart3, ScrollText, AlertCircle
} from 'lucide-react';
import request from '../../api/request';

// ==================== 类型定义 ====================

interface LLMProviderItem {
  id: number;
  provider: string;
  model_name: string;
  model_url: string;
  model_url_masked: string;
  api_key: string;
  temperature: string;
  is_active: boolean;
  total_tokens_used: number;
  reserved_tokens: number;
  thinking_enabled: boolean;
  use_for_order_analysis: boolean;
  order_analysis_thinking_enabled: boolean;
  sort_order: number;
}

interface LLMLogItem {
  id: number;
  provider: string;
  model_name: string;
  log_type: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  message: string;
  timestamp: string;
}

interface LLMSettingsData {
  polling_enabled: boolean;
  polling_provider: string | null;
  token_threshold: number;
  max_log_entries: number;
}

interface ModelConfigModalProps {
  onClose: () => void;
}

// ==================== 常量 ====================

const PROVIDERS = [
  { key: 'doubao', label: '豆包', color: 'from-blue-500 to-cyan-400', icon: '🫘' },
  { key: 'qwen', label: '千问', color: 'from-purple-500 to-pink-400', icon: '🔮' },
  { key: 'openai', label: 'OpenAI', color: 'from-green-500 to-emerald-400', icon: '🤖' },
  { key: 'gemini', label: 'Gemini', color: 'from-orange-500 to-yellow-400', icon: '✨' },
];

const LOG_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  token_usage: { label: 'Token', color: 'text-blue-400' },
  model_switch: { label: '切换', color: 'text-yellow-400' },
  config_change: { label: '配置', color: 'text-green-400' },
  auto_switch: { label: '自动切换', color: 'text-orange-400' },
  daily_reset: { label: '重置', color: 'text-purple-400' },
};

// ==================== 子组件 ====================

// 新增模型表单
const AddModelForm: React.FC<{
  provider: string;
  onAdded: () => void;
}> = ({ provider, onAdded }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({ model_name: '', model_url: '', api_key: '', temperature: '0.7', reserved_tokens: 0, thinking_enabled: false });
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.model_name || !form.model_url || !form.api_key) return;
    setIsSaving(true);
    try {
      await request.post('/api/settings/llm/providers', { provider, ...form });
      setForm({ model_name: '', model_url: '', api_key: '', temperature: '0.7', reserved_tokens: 0, thinking_enabled: false });
      setIsOpen(false);
      onAdded();
    } catch (e) {
      console.error('添加模型失败:', e);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-border rounded-lg text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all duration-200"
      >
        <Plus className="w-4 h-4" />
        添加模型
      </button>
    );
  }

  return (
    <div className="border border-primary/30 rounded-lg p-4 space-y-3 bg-primary/5 animate-slide-in">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-primary">添加新模型</span>
        <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>
      <input
        placeholder="模型名称 (如: doubao-seed-2-0-pro)"
        value={form.model_name}
        onChange={e => setForm({ ...form, model_name: e.target.value })}
        className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
      />
      <input
        placeholder="API URL (如: https://ark.cn-beijing.volces.com/api/v3)"
        value={form.model_url}
        onChange={e => setForm({ ...form, model_url: e.target.value })}
        className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
      />
      <input
        placeholder="API Key"
        value={form.api_key}
        onChange={e => setForm({ ...form, api_key: e.target.value })}
        className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
        type="password"
      />
      <div className="flex gap-3 items-center">
        <label className="text-xs text-muted-foreground whitespace-nowrap">温度:</label>
        <input
          type="number"
          step="0.1"
          min="0"
          max="2"
          value={form.temperature}
          onChange={e => setForm({ ...form, temperature: e.target.value })}
          className="w-20 bg-input border border-border rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
        />
        <label className="text-xs text-muted-foreground whitespace-nowrap">预留Token:</label>
        <input
          type="number"
          min="0"
          step="10000"
          value={form.reserved_tokens}
          onChange={e => setForm({ ...form, reserved_tokens: parseInt(e.target.value) || 0 })}
          className="w-24 bg-input border border-border rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">深度思考 (Extended Thinking)</span>
        <button
          type="button"
          onClick={() => setForm({ ...form, thinking_enabled: !form.thinking_enabled })}
          className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${form.thinking_enabled ? 'bg-purple-500' : 'bg-muted'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${form.thinking_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
        </button>
      </div>
      <button
        onClick={handleSubmit}
        disabled={isSaving || !form.model_name || !form.model_url || !form.api_key}
        className="w-full py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {isSaving ? '保存中...' : '保存'}
      </button>
    </div>
  );
};

// 模型卡片（聊天轮询模型）
const ModelCard: React.FC<{
  model: LLMProviderItem;
  onActivate: (id: number) => void;
  onDelete: (id: number) => void;
  onMoveUp?: (id: number) => void;
  onMoveDown?: (id: number) => void;
  isFirst?: boolean;
  isLast?: boolean;
  onEdit?: (model: LLMProviderItem) => void;
  onToggleOrderAnalysis?: (id: number) => void;
  onToggleOrderAnalysisThinking?: (id: number, currentValue: boolean) => void;
}> = ({ model, onActivate, onDelete, onMoveUp, onMoveDown, isFirst, isLast, onEdit, onToggleOrderAnalysis, onToggleOrderAnalysisThinking }) => {
  const tokenK = model.total_tokens_used >= 1000
    ? `${(model.total_tokens_used / 1000).toFixed(1)}K`
    : model.total_tokens_used.toString();
  const tokenM = model.total_tokens_used >= 1000000
    ? `${(model.total_tokens_used / 1000000).toFixed(2)}M`
    : tokenK;
  const reservedDisplay = model.reserved_tokens > 0
    ? `${model.reserved_tokens >= 10000 ? `${(model.reserved_tokens / 10000).toFixed(0)}万` : model.reserved_tokens.toLocaleString()}`
    : null;

  return (
    <div
      className={`relative rounded-lg border p-3 transition-all duration-200 ${model.is_active
          ? 'border-primary/60 bg-primary/10 shadow-[0_0_15px_rgba(124,58,237,0.15)]'
          : 'border-border bg-card hover:border-border/80'
        }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {model.is_active && (
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground flex-shrink-0">
              <Check className="w-3 h-3" />
            </span>
          )}
          <span className={`text-sm font-medium truncate ${model.is_active ? 'text-primary' : 'text-foreground'}`}>
            {model.model_name}
          </span>
          {model.use_for_order_analysis && (
            <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-500/20 text-green-400 border border-green-500/30">
              订单分析
            </span>
          )}
          {model.thinking_enabled && (
            <span className="text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded flex-shrink-0" title="深度思考已启用">
              🧠
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {onToggleOrderAnalysis && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleOrderAnalysis(model.id); }}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${model.use_for_order_analysis ? 'bg-green-500' : 'bg-muted'}`}
              title={model.use_for_order_analysis ? '关闭订单分析' : '开启订单分析（自动预留25万Token）'}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-md transition-transform ${model.use_for_order_analysis ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          )}
          {model.use_for_order_analysis && onToggleOrderAnalysisThinking && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleOrderAnalysisThinking(model.id, model.order_analysis_thinking_enabled); }}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${model.order_analysis_thinking_enabled ? 'bg-purple-500' : 'bg-muted'}`}
              title={model.order_analysis_thinking_enabled ? '关闭订单分析深度思考' : '开启订单分析深度思考'}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-md transition-transform ${model.order_analysis_thinking_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          )}
          {onEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(model); }}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
              title="编辑"
            >
              <Settings2 className="w-3.5 h-3.5" />
            </button>
          )}
          {onMoveUp && (
            <button
              onClick={(e) => { e.stopPropagation(); onMoveUp(model.id); }}
              disabled={isFirst}
              className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded disabled:opacity-30 transition-colors"
              title="上移"
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
          )}
          {onMoveDown && (
            <button
              onClick={(e) => { e.stopPropagation(); onMoveDown(model.id); }}
              disabled={isLast}
              className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded disabled:opacity-30 transition-colors"
              title="下移"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          )}
          {!model.is_active && (
            <button
              onClick={() => onActivate(model.id)}
              className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
              title="激活此模型"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => {
              if (window.confirm(`确定删除模型 "${model.model_name}"?`)) {
                onDelete(model.id);
              }
            }}
            className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
            title="删除"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="truncate mr-2" title={model.api_key}>{model.api_key}</span>
        <span className="flex items-center gap-1 flex-shrink-0">
          <BarChart3 className="w-3 h-3" />
          {tokenM}
        </span>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
        <span className="truncate" title={model.model_url}>{model.model_url_masked}</span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {reservedDisplay && (
            <span className="text-orange-400" title={`预留 ${model.reserved_tokens.toLocaleString()} tokens`}>
              预留:{reservedDisplay}
            </span>
          )}
          <span>T:{model.temperature}</span>
        </div>
      </div>
    </div>
  );
};

// ==================== 主组件 ====================

const ModelConfigModal: React.FC<ModelConfigModalProps> = ({ onClose }) => {
  // 当前选中的厂商 Tab
  const [activeTab, setActiveTab] = useState('doubao');
  // 当前的子页面: models | logs
  const [subView, setSubView] = useState<'models' | 'logs'>('models');
  // 数据
  const [providers, setProviders] = useState<LLMProviderItem[]>([]);
  const [logs, setLogs] = useState<LLMLogItem[]>([]);
  const [llmSettings, setLlmSettings] = useState<LLMSettingsData>({
    polling_enabled: false,
    polling_provider: null,
    token_threshold: 2000000,
    max_log_entries: 5000,
  });
  const [isLoading, setIsLoading] = useState(true);
  // 编辑模型
  const [editingModel, setEditingModel] = useState<LLMProviderItem | null>(null);
  const [editForm, setEditForm] = useState({ model_name: '', model_url: '', api_key: '', temperature: '0.7', reserved_tokens: 0, thinking_enabled: false, order_analysis_thinking_enabled: false });
  const [editSaving, setEditSaving] = useState(false);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // 加载数据
  const fetchData = async () => {
    try {
      const [provRes, settingsRes] = await Promise.all([
        request.get('/api/settings/llm/providers'),
        request.get('/api/settings/llm/settings'),
      ]);
      setProviders(provRes.data.providers || []);
      setLlmSettings(settingsRes.data);
    } catch (e) {
      console.error('加载 LLM 配置失败:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await request.get(`/api/settings/llm/logs?provider=${activeTab}`);
      setLogs(res.data.logs || []);
      setTimeout(() => {
        if (logContainerRef.current) {
          logContainerRef.current.scrollTop = 0;
        }
      }, 100);
    } catch (e) {
      console.error('加载日志失败:', e);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (subView === 'logs') {
      fetchLogs();
    }
  }, [subView, activeTab]);

  // 编辑模型
  const handleEdit = (model: LLMProviderItem) => {
    setEditingModel(model);
    setEditForm({
      model_name: model.model_name,
      model_url: model.model_url,
      api_key: '',
      temperature: model.temperature,
      reserved_tokens: model.reserved_tokens,
      thinking_enabled: model.thinking_enabled,
      order_analysis_thinking_enabled: model.order_analysis_thinking_enabled || false,
    });
  };

  const handleUpdateModel = async () => {
    if (!editingModel || !editForm.model_name || !editForm.model_url) return;
    setEditSaving(true);
    try {
      const body: any = { ...editForm };
      if (!body.api_key) delete body.api_key;
      await request.put(`/api/settings/llm/providers/${editingModel.id}`, body);
      setEditingModel(null);
      fetchData();
    } catch (e) {
      console.error('更新模型失败:', e);
    } finally {
      setEditSaving(false);
    }
  };

  const handleToggleOrderAnalysis = async (id: number) => {
    try {
      await request.post(`/api/settings/llm/providers/${id}/toggle-order-analysis`);
      fetchData();
    } catch (e) {
      console.error('切换订单分析开关失败:', e);
    }
  };

  const handleToggleOrderAnalysisThinking = async (id: number, currentValue: boolean) => {
    try {
      await request.put(`/api/settings/llm/providers/${id}`, {
        order_analysis_thinking_enabled: !currentValue,
      });
      fetchData();
    } catch (e) {
      console.error('切换订单分析思考开关失败:', e);
    }
  };

  // 操作
  const handleActivate = async (id: number) => {
    try {
      await request.post(`/api/settings/llm/providers/${id}/activate`);
      fetchData();
    } catch (e) {
      console.error('激活模型失败:', e);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await request.delete(`/api/settings/llm/providers/${id}`);
      fetchData();
    } catch (e) {
      console.error('删除模型失败:', e);
    }
  };

  const handleMoveUp = async (id: number) => {
    try {
      await request.post(`/api/settings/llm/providers/${id}/move?direction=up`);
      fetchData();
    } catch (e) {
      console.error('上移模型失败:', e);
    }
  };

  const handleMoveDown = async (id: number) => {
    try {
      await request.post(`/api/settings/llm/providers/${id}/move?direction=down`);
      fetchData();
    } catch (e) {
      console.error('下移模型失败:', e);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('确定清空所有 LLM 日志?')) return;
    try {
      await request.delete('/api/settings/llm/logs');
      setLogs([]);
    } catch (e) {
      console.error('清空日志失败:', e);
    }
  };

  const handleUpdateSettings = async (updates: Partial<LLMSettingsData>) => {
    const newSettings = { ...llmSettings, ...updates };
    setLlmSettings(newSettings);
    try {
      await request.put('/api/settings/llm/settings', newSettings);
    } catch (e) {
      console.error('更新设置失败:', e);
      setLlmSettings(llmSettings); // 回滚
    }
  };

  // 当前厂商的模型
  const currentModels = providers.filter(p => p.provider === activeTab);
  const currentProviderInfo = PROVIDERS.find(p => p.key === activeTab)!;

  // 总 Token
  const totalTokens = currentModels.reduce((sum, m) => sum + m.total_tokens_used, 0);
  const totalTokensDisplay = totalTokens >= 1000000
    ? `${(totalTokens / 1000000).toFixed(2)}M`
    : totalTokens >= 1000
      ? `${(totalTokens / 1000).toFixed(1)}K`
      : totalTokens.toString();

  const formatTime = (isoString: string) => {
    try {
      const d = new Date(isoString);
      return d.toLocaleString('zh-CN', { hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return isoString; }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col border border-border/50 overflow-hidden">

        {/* ===== Header ===== */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-gradient-to-r from-card to-secondary/30">
          <h2 className="text-lg font-bold flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-primary/20">
              <Cpu className="w-5 h-5 text-primary" />
            </div>
            模型配置
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ===== 厂商 Tabs ===== */}
        <div className="flex border-b border-border">
          {PROVIDERS.map(prov => {
            const isActive = activeTab === prov.key;
            const provModels = providers.filter(p => p.provider === prov.key);
            const hasActive = provModels.some(p => p.is_active);
            return (
              <button
                key={prov.key}
                onClick={() => setActiveTab(prov.key)}
                className={`flex-1 flex flex-col items-center py-2.5 text-xs font-medium transition-all duration-200 relative
                  ${isActive
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground/70'
                  }`}
              >
                <span className="text-lg mb-0.5">{prov.icon}</span>
                <span>{prov.label}</span>
                {provModels.length > 0 && (
                  <span className={`absolute top-1 right-2 w-2 h-2 rounded-full ${hasActive ? 'bg-accent' : 'bg-muted-foreground/50'}`} />
                )}
                {isActive && (
                  <div className={`absolute bottom-0 left-1/4 right-1/4 h-0.5 rounded-full bg-gradient-to-r ${prov.color}`} />
                )}
              </button>
            );
          })}
        </div>

        {/* ===== 子导航: 模型 / 日志 ===== */}
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-border/50 bg-secondary/20">
          <div className="flex gap-1">
            <button
              onClick={() => setSubView('models')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${subView === 'models'
                  ? 'bg-primary/20 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
            >
              <Settings2 className="w-3.5 h-3.5" />
              模型管理
            </button>
            <button
              onClick={() => setSubView('logs')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${subView === 'logs'
                  ? 'bg-primary/20 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
            >
              <ScrollText className="w-3.5 h-3.5" />
              日志
            </button>
          </div>
          {/* Token 统计 */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <BarChart3 className="w-3.5 h-3.5" />
            <span>Token: <span className="text-foreground font-medium">{totalTokensDisplay}</span></span>
          </div>
        </div>

        {/* ===== 内容区 ===== */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : subView === 'models' ? (
            <div className="p-4 space-y-3">
              {/* 当前激活模型 - 下拉切换 */}
              {currentModels.length > 0 && (
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-xs text-muted-foreground whitespace-nowrap">当前模型:</label>
                  <select
                    value={currentModels.find(m => m.is_active)?.id || ''}
                    onChange={e => {
                      const id = parseInt(e.target.value);
                      if (id) handleActivate(id);
                    }}
                    className="flex-1 bg-input border border-border rounded-md px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
                  >
                    {currentModels.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.model_name} ({(m.total_tokens_used / 1000).toFixed(0)}K tokens)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* 模型列表 */}
              {currentModels.map((model, index) => (
                <ModelCard
                  key={model.id}
                  model={model}
                  onActivate={handleActivate}
                  onDelete={handleDelete}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  isFirst={index === 0}
                  isLast={index === currentModels.length - 1}
                  onEdit={handleEdit}
                  onToggleOrderAnalysis={handleToggleOrderAnalysis}
                  onToggleOrderAnalysisThinking={handleToggleOrderAnalysisThinking}
                />
              ))}

              {currentModels.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">尚未配置 {currentProviderInfo.label} 模型</p>
                  <p className="text-xs mt-1">点击下方「添加模型」开始配置</p>
                </div>
              )}

              {/* 添加模型 */}
              <AddModelForm provider={activeTab} onAdded={fetchData} />
            </div>
          ) : (
            /* 日志视图 */
            <div className="flex flex-col h-full">
              {/* 日志工具栏 */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-border/50">
                <span className="text-xs text-muted-foreground">{logs.length} 条日志</span>
                <div className="flex gap-2">
                  <button
                    onClick={fetchLogs}
                    className="flex items-center gap-1 px-2 py-1 text-xs border border-border rounded-md hover:bg-muted transition-colors"
                  >
                    <RefreshCw className="w-3 h-3" />
                    刷新
                  </button>
                  <button
                    onClick={handleClearLogs}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-destructive border border-destructive/30 rounded-md hover:bg-destructive/10 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    清空
                  </button>
                </div>
              </div>
              {/* 日志列表 */}
              <div
                ref={logContainerRef}
                className="flex-1 overflow-y-auto px-4 py-2 space-y-1.5 font-mono text-[11px]"
                style={{ maxHeight: '300px' }}
              >
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    暂无日志
                  </div>
                ) : (
                  logs.map(log => {
                    const typeInfo = LOG_TYPE_LABELS[log.log_type] || { label: log.log_type, color: 'text-muted-foreground' };
                    return (
                      <div key={log.id} className="flex gap-2 py-1 border-b border-border/30 last:border-0">
                        <span className="text-muted-foreground flex-shrink-0 w-28">
                          {formatTime(log.timestamp)}
                        </span>
                        <span className={`flex-shrink-0 w-16 ${typeInfo.color}`}>
                          [{typeInfo.label}]
                        </span>
                        <span className="break-all text-foreground/80">
                          {log.message}
                          {log.total_tokens > 0 && (
                            <span className="text-muted-foreground ml-1">
                              ({log.total_tokens.toLocaleString()} tokens)
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* ===== Footer: 轮询设置 ===== */}
        <div className="border-t border-border px-5 py-3 space-y-2.5 bg-secondary/20">
          {/* 轮询开关 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 ${llmSettings.polling_enabled ? 'text-accent animate-spin' : 'text-muted-foreground'}`}
                style={llmSettings.polling_enabled ? { animationDuration: '3s' } : {}} />
              <div>
                <span className="text-sm font-medium">轮询自动切换</span>
                <p className="text-[10px] text-muted-foreground">
                  Token 达 {(llmSettings.token_threshold / 10000).toFixed(0)} 万时自动切换同厂商下一个模型，北京时间 0 点重置
                </p>
              </div>
            </div>
            <button
              onClick={() => handleUpdateSettings({
                polling_enabled: !llmSettings.polling_enabled,
                polling_provider: !llmSettings.polling_enabled ? activeTab : llmSettings.polling_provider
              })}
              className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${llmSettings.polling_enabled ? 'bg-accent' : 'bg-muted'
                }`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${llmSettings.polling_enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
            </button>
          </div>

          {/* 轮询详细配置 - 展开显示 */}
          {llmSettings.polling_enabled && (
            <div className="flex gap-3 items-center text-xs animate-slide-in">
              <div className="flex items-center gap-1.5">
                <label className="text-muted-foreground whitespace-nowrap">生效厂商:</label>
                <select
                  value={llmSettings.polling_provider || activeTab}
                  onChange={e => handleUpdateSettings({ polling_provider: e.target.value })}
                  className="bg-input border border-border rounded px-2 py-1 text-xs focus:ring-1 focus:ring-ring focus:outline-none"
                >
                  {PROVIDERS.map(p => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-muted-foreground whitespace-nowrap">阈值(万):</label>
                <input
                  type="number"
                  min="10"
                  step="10"
                  value={Math.round(llmSettings.token_threshold / 10000)}
                  onChange={e => handleUpdateSettings({ token_threshold: parseInt(e.target.value || '200') * 10000 })}
                  className="w-16 bg-input border border-border rounded px-2 py-1 text-xs focus:ring-1 focus:ring-ring focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-muted-foreground whitespace-nowrap">日志上限:</label>
                <input
                  type="number"
                  min="100"
                  step="100"
                  value={llmSettings.max_log_entries}
                  onChange={e => handleUpdateSettings({ max_log_entries: parseInt(e.target.value || '5000') })}
                  className="w-20 bg-input border border-border rounded px-2 py-1 text-xs focus:ring-1 focus:ring-ring focus:outline-none"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== 编辑模型弹窗 ===== */}
      {editingModel && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10 rounded-xl" onClick={() => setEditingModel(null)}>
          <div className="bg-card rounded-lg shadow-xl w-full max-w-sm mx-4 p-5 border border-border" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground">编辑模型 - {editingModel.model_name}</h3>
              <button onClick={() => setEditingModel(null)} className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">模型名称</label>
                <input
                  type="text"
                  value={editForm.model_name}
                  onChange={e => setEditForm({ ...editForm, model_name: e.target.value })}
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">API URL</label>
                <input
                  type="text"
                  value={editForm.model_url}
                  onChange={e => setEditForm({ ...editForm, model_url: e.target.value })}
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">API Key (留空不修改)</label>
                <input
                  type="password"
                  value={editForm.api_key}
                  onChange={e => setEditForm({ ...editForm, api_key: e.target.value })}
                  placeholder="留空则不修改"
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Temperature</label>
                <select
                  value={editForm.temperature}
                  onChange={e => setEditForm({ ...editForm, temperature: e.target.value })}
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
                >
                  {['0.1', '0.2', '0.3', '0.4', '0.5', '0.6', '0.7', '0.8', '0.9', '1.0'].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">预留 Token</label>
                <input
                  type="number"
                  min="0"
                  step="10000"
                  value={editForm.reserved_tokens}
                  onChange={e => setEditForm({ ...editForm, reserved_tokens: parseInt(e.target.value) || 0 })}
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">深度思考 (Extended Thinking)</span>
                <button
                  type="button"
                  onClick={() => setEditForm({ ...editForm, thinking_enabled: !editForm.thinking_enabled })}
                  className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${editForm.thinking_enabled ? 'bg-purple-500' : 'bg-muted'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${editForm.thinking_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">订单分析深度思考 (Order Analysis Thinking)</span>
                <button
                  type="button"
                  onClick={() => setEditForm({ ...editForm, order_analysis_thinking_enabled: !editForm.order_analysis_thinking_enabled })}
                  className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${editForm.order_analysis_thinking_enabled ? 'bg-purple-500' : 'bg-muted'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${editForm.order_analysis_thinking_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <button
                onClick={handleUpdateModel}
                disabled={editSaving || !editForm.model_name || !editForm.model_url}
                className="w-full py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {editSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelConfigModal;
